import React, { useState, useEffect } from "react";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import {
    useGetAllUnverifiedProfilesMutation,
    useGetAllApprovalListMutation,
    useSetUserApproverMutation,
    useDeleteLotusUserMutation,
} from "../../../features/tenant/userPermissions/userPermissions";

const idToZoneMapping = {
    202: "East",
    200: "South",
    199: "North",
    201: "West",
};

const AdminApprovalList = () => {
    const [unverifiedUsers, setUnverifiedUsers] = useState([]);
    const [approverList, setApproverList] = useState([]);
    const [selectedApprover, setSelectedApprover] = useState({});
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [offset, setOffset] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const [
        getAllUnverifiedProfiles,
        {
            data: unverifiedUsersData,
            isLoading: unverifiedUsersLoading,
        },
    ] = useGetAllUnverifiedProfilesMutation();

    const [
        getAllApprovalList,
        {
            data: approverListData,
        },
    ] = useGetAllApprovalListMutation();

    const [
        setUserApprover,
        {
            isLoading: isSetUserApproverLoading,
        }
    ] = useSetUserApproverMutation();

    const [
        deleteLotusUser,
        {
            isLoading: isDeleting,
        }
    ] = useDeleteLotusUserMutation();


    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
            setOffset(0);
        }, 500);
        return () => clearTimeout(handler);
    }, [search]);

    // Fetch main approval list
    const fetchApprovalTableData = async () => {
        getAllApprovalList({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            limit: perPage,
            offset: offset,
            search: debouncedSearch,
            dateFrom: dateFrom,
            dateTo: dateTo,
        });
    };

    // Fetch employee list for the approver dropdown
    const fetchEmployeeList = async () => {
        getAllUnverifiedProfiles({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        });
    };

    useEffect(() => {
        fetchApprovalTableData();
    }, [perPage, offset, debouncedSearch, dateFrom, dateTo]);

    useEffect(() => {
        fetchEmployeeList();
    }, []);

    useEffect(() => {
        console.log(approverListData, "approverListData");
        if (approverListData?.success) {
            // Main table data now comes from getAllApprovalList
            const usersArray = Array.isArray(approverListData.body?.approvalList)
                ? approverListData.body.approvalList
                : Array.isArray(approverListData.body)
                    ? approverListData.body
                    : [];

            setUnverifiedUsers(usersArray);
            setTotalRows(approverListData.body?.totalCount || usersArray.length);

            // Pre-fill selected approvers if they have one
            const initialApprovers = {};
            usersArray.forEach(user => {
                if (user.added_by_id) {
                    initialApprovers[user.id] = user.added_by_id;
                }
            });
            setSelectedApprover(prev => ({ ...prev, ...initialApprovers }));
        }
    }, [approverListData]);

    useEffect(() => {
        if (unverifiedUsersData?.success) {
            // Dropdown list now uses unverifiedUsersData (or whichever API returns employees)
            // Note: If unverifiedUsersData is not employees, this might need further adjustment
            const employees = Array.isArray(unverifiedUsersData.body?.unverifiedUsers)
                ? unverifiedUsersData.body?.unverifiedUsers
                : unverifiedUsersData.body || [];
            setApproverList(employees);
        }
    }, [unverifiedUsersData]);

    // Handle approver selection change
    const handleApproverChange = (userId, approverId) => {
        setSelectedApprover(prev => ({
            ...prev,
            [userId]: approverId
        }));
    };

    // Handle assigning approver
    const handleAssignApprover = async (userId) => {
        const approverId = selectedApprover[userId];
        if (!approverId) {
            alert("Please select an approver first");
            return;
        }

        try {
            const response = await setUserApprover({
                token: sessionStorage.getItem("access_token"),
                tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
                data: {
                    approval_id: userId,
                    approver_id: approverId
                }
            });

            if (response.data?.success) {
                alert("Approver assigned successfully!");
                fetchApprovalTableData();
            } else {
                alert(response.error?.data?.message || "Failed to assign approver");
            }
        } catch (error) {
            console.error("Error assigning approver:", error);
            alert("Failed to assign approver");
        }
    };

    // Handle delete user
    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) return;

        try {
            const response = await deleteLotusUser({
                token: sessionStorage.getItem("access_token"),
                tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
                data: { id: userId }
            });

            if (response.data?.success) {
                alert("User deleted successfully");
                fetchApprovalTableData();
            } else {
                alert(response.error?.data?.message || "Failed to delete user");
            }
        } catch (error) {
            console.error("Error deleting user:", error);
            alert("Failed to delete user");
        }
    };

    // Define columns for DataTable
    const columns = [
        {
            name: "ID",
            selector: row => row.id,
            sortable: true,
            width: "80px"
        },
        {
            name: "Reg. Date",
            selector: row => row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A",
            sortable: true,
            width: "120px"
        },
        {
            name: "Registered From",
            cell: row => {
                const platform = (row.platform_name || "").toUpperCase();
                if (!platform) return <span className="text-gray-400">—</span>;
                const label = platform === "BD" ? "Biod" : platform === "LP" ? "Lotus Professional" : platform;
                const colorClass = platform === "BD" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800";
                return (
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colorClass}`}>
                        {label}
                    </span>
                );
            },
            sortable: true,
            width: "160px"
        },
        {
            name: "Status",
            cell: row => (
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${row.status === '1' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {row.status === '1' ? 'Success' : 'Pending'}
                </span>
            ),
            width: "100px"
        },
        {
            name: "Name",
            selector: row => row.name,
            sortable: true,
            width: "180px"
        },
        {
            name: "Mobile",
            selector: row => row.mobile,
            sortable: true,
            width: "130px"
        },
        {
            name: "Email",
            selector: row => row.email,
            sortable: true,
            width: "200px"
        },
        {
            name: "Salon Name",
            selector: row => row.salon_name || "N/A",
            sortable: true,
            width: "180px"
        },
        {
            name: "Salon ID",
            selector: row => row.salon_id || "N/A",
            sortable: true,
        },
        {
            name: "Salon Address",
            cell: row => (
                <span title={row.salon_address || "N/A"} style={{ cursor: 'pointer' }}>
                    {row.salon_address || "N/A"}
                </span>
            ),
            sortable: true,
            width: "200px"
        },
        {
            name: "Salon Zone",
            selector: row => idToZoneMapping[Number(row.added_by_id)] || row.salon_zone || row.zone_name || "N/A",
            sortable: true,
            width: "130px"
        },
        {
            name: "Salon State",
            selector: row => row.salon_state || "N/A",
            sortable: true,
            width: "130px"
        },
        {
            name: "User Type",
            selector: row => row.user_type,
            sortable: true,
            width: "130px"
        },
        {
            name: "Select Approver",
            cell: row => row.status !== '1' ? (
                <select
                    className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                    value={selectedApprover[row.id] || ""}
                    onChange={(e) => handleApproverChange(row.id, e.target.value)}
                >
                    <option value="">-- Select Approver --</option>
                    {/* {approverList.map(approver => (
                        <option key={approver.id} value={approver.panel_user_id}>
                            {approver.name} ({approver.designation})
                        </option>
                    ))} */}
                </select>
            ) : (
                <span className="text-gray-400">N/A</span>
            ),
            width: "220px"
        },
        {
            name: "Action",
            cell: row => row.status !== '1' ? (
                <div className="flex gap-2">
                    <button
                        onClick={() => handleAssignApprover(row.id)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-xs uppercase font-medium"
                        disabled={isSetUserApproverLoading}
                    >
                        {isSetUserApproverLoading ? "..." : "Assign"}
                    </button>
                    <button
                        onClick={() => handleDeleteUser(row.id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-xs uppercase font-medium"
                        disabled={isDeleting}
                    >
                        {isDeleting ? "..." : "Delete"}
                    </button>
                </div>
            ) : null,
            width: "160px"
        }
    ];

    const handlePageChange = (page) => {
        setOffset((page - 1) * perPage);
    };

    const handlePerRowsChange = (newPerPage) => {
        setPerPage(newPerPage);
        setOffset(0);
    };

    return (
        <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
            <h1 className="text-2xl font-bold mb-6 text-gray-800">Admin Approval List</h1>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search User/Salon</label>
                        <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                            placeholder="Name, Mobile, Salon..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                        <input
                            type="date"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                        <input
                            type="date"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                        />
                    </div>
                    <div>
                        <button
                            onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setOffset(0); }}
                            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors w-full"
                        >
                            Reset Filters
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <DataTableComponent
                    columns={columns}
                    data={unverifiedUsers}
                    loading={unverifiedUsersLoading}
                    pagination
                    paginationServer
                    totalRows={totalRows}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                    paginationPerPage={perPage}
                    title="User Registration Management"
                />
            </div>
        </div>
    );
};

export default AdminApprovalList;